import { useRecuperarAutorizacionPorIdQuery } from '../../../../redux/api'
import toast, { Toaster } from 'react-hot-toast'
import { useAutorizacionForm } from '../../models/autorizacionForm.type'
import { AppConstantes } from '../../../../utils/Constantes'
import { useFieldArray } from 'react-hook-form'
import { Modal } from 'react-bootstrap'
import { useEffect, useState } from 'react'
import { ReportesCumplimiento } from '../reportes/ReportesCumplimiento'
import { FileDropZone } from '../../../../components/forms/dropzone/fileDropZone.type'
import { useParams } from 'react-router-dom'
import {
  useGuardarReportesMutation,
  useRecuperarReportesPorIdRelacionadoMutation,
} from '../../../../redux/api/reportesCumplimiento.api'
import { useDispatch } from 'react-redux'
import { cargarReportes } from '../../../../redux/slices/reportes'
import { style } from '../../../recordatorios/models/recordatorioForm.type'
import ComponentePrivado from '../../../../components/roles/ComponentePrivado'
import { Roles } from '../../../../utils/Roles'
import useHasPermiso from '../../../../hooks/autenticacion/useHasPermiso'
import { Reporte } from '../../../../models'

type Props = {
  idRelacionado: string | undefined
  titulo: string | undefined
  nombre: string | undefined
  show: boolean
  isModoEditar: boolean
  close: () => void
}

export const ModalAgregarEditarReportes = ({
  idRelacionado,
  titulo,
  nombre,
  close,
  show,
  isModoEditar,
}: Props) => {
  const { hasPermiso } = useHasPermiso()
  const usuarioPermiso = hasPermiso([Roles.roleAutorizacionesCrear])

  const formAutorizacion = useAutorizacionForm()
  const { control, reset, resetField } = formAutorizacion

  const { isSuccess, refetch } = useRecuperarAutorizacionPorIdQuery(
    idRelacionado ? idRelacionado : '',
    {}
  )

  const [recuperarReportes, { isLoading }] = useRecuperarReportesPorIdRelacionadoMutation()
  const [guardarReportes] = useGuardarReportesMutation()

  const dispatch = useDispatch()

  const { idAutorizacion } = useParams()
  const [arrArchivos, setArrArchivos] = useState([] as FileDropZone[])

  const tamanioModal = usuarioPermiso ? 'lg' : undefined
  
  useEffect(() => {
    if (isModoEditar && isSuccess) {
      // Hidrata el slice `reportes` con los documentos ya asociados a la autorización.
      recuperarReportes(idAutorizacion)
        .unwrap()
        .then((resp: Reporte[]) => {
          dispatch(cargarReportes(resp))
        })
        .catch(() => {})
    }
  }, [isSuccess])

  const { remove } = useFieldArray({
    name: 'recordatorios',
    control,
  })

  /**
   * @description Cierra la ventana modal reseteando todos los campos
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 04/11/2022
   */
  const cerrarModal = () => {
    reset()
    remove()
    resetField('recordatorios')
    close()
    // Refresca autorización para que el detalle muestre evidencia actual.
    refetch()
  }

  return (
    <div>
      <Toaster />
      <Modal
        show={!isLoading && show}
        size={tamanioModal}
        onHide={cerrarModal}
        dialogClassName='modal-dialog'
        keyboard={false}
        backdrop='static'>
        <Modal.Header>
          <div className='align-items-center d-flex flex-grow-1 '>
            {titulo ? <h4 className='modal-tittle'>{titulo}</h4> : ''}
            {nombre ? (
              <span className='text-muted fw-lighter fs-4 ps-2 d-flex'>| {nombre} </span>
            ) : (
              ''
            )}
          </div>
          <button
            className=' close btn  btn-outline-dark btn-icon btn-circle btn-sm ms-5 d-flex col-12 '
            onClick={cerrarModal}>
            <i className='fa-solid fa-plus fs-1' style={{ rotate: '45deg' }}></i>
          </button>
        </Modal.Header>

        <Modal.Body>
          <ReportesCumplimiento
            arrArchivos={arrArchivos}
            setArrArchivos={setArrArchivos}
            idRelacionado={idAutorizacion ?? ''}
            usuarioPermisoCrear={usuarioPermiso}
            textoTipoArchivo={'No hay documentos asociados'}
          />
        </Modal.Body>
        <Modal.Footer>
          <button type='reset' className=' btn  text-hover-dark btn-cancelar' onClick={cerrarModal}>
            Cerrar
          </button>

          <ComponentePrivado rolMostrarPorPermiso={[Roles.roleAutorizacionesCrear]}>
            <button
              disabled={arrArchivos.length === 0}
              className='btn btn-primary p-3'
              type='submit'
              onClick={() => {
                // Contrato backend de carga de reportes: requiere id_relacionado + tipo + desc_tipo,
                // y los archivos se envían como `archivos`.
                const formData = new FormData()
                formData.append('id_relacionado', idAutorizacion ?? '')
                formData.append('tipo', AppConstantes.CVE_REPORTE_TIPO_AUTORIZACION)
                formData.append('desc_tipo', AppConstantes.DESC_REPORTE_TIPO_AUTORIZACION)
                const toastId = toast.loading('Actualizando reportes', { style })
                if (arrArchivos && arrArchivos.length > 0) {
                  refetch()
                  arrArchivos.forEach((a) => {
                    if (a.isNew && a.file) {
                      // Sólo se envían archivos nuevos.
                      formData.append('archivos', a.file, a.file.name)
                    }
                  })
                  guardarReportes(formData)
                    .unwrap()
                    .then(() => {
                      recuperarReportes(idAutorizacion)
                        .unwrap()
                        .then((resp: Reporte[]) => {
                          dispatch(cargarReportes(resp))
                          setArrArchivos([])
                          toast.success('¡Reportes actualizados!', {
                            id: toastId,
                            style,
                          })
                        })
                        .catch()
                    })
                    .catch(() => {
                      // Mantiene el modal abierto para reintentar carga.
                      toast.error(
                        'Existen algunos reportes sin guardar, verifíquelos y guárdelos de nuevo.',
                        {
                          id: toastId,
                          style,
                        }
                      )
                    })
                } else {
                  toast.success('¡Reportes actualizados!', { style })
                }
              }}>
              Guardar
            </button>
          </ComponentePrivado>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
